// Phase M1 — Club Members Management
// Single-page admin hub replacing the legacy Players tab. Owns:
//   • Dashboard cards (health-at-a-glance)
//   • Members table with color-coded status pills
//   • Filters + search (name / email / phone)
//   • Slide-out profile drawer (Notion / Linear style)
//   • Row actions: impersonate, add to ladder, regenerate/resend/copy invite,
//     deactivate/reactivate
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Users, UserPlus, UserCircle, MagnifyingGlass, Warning, CaretDown, DotsThreeVertical,
  Trophy, EnvelopeSimple, LinkSimple, ArrowClockwise, PaperPlaneTilt, Copy, UserSwitch,
  Prohibit, CheckCircle, X, CalendarBlank, IdentificationBadge, ArrowSquareOut, EnvelopeOpen,
} from '@phosphor-icons/react';
import { adminAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import ConfirmDialog from '../components/events/ConfirmDialog';
import AwaitingApprovalSection from '../components/admin/AwaitingApprovalSection';
import InvitesPanel from '../components/admin/InvitesPanel';

// -------------------- Status pill helpers --------------------
function Pill({ tone = 'gray', children, testid }) {
  const map = {
    green:  'bg-[#e6f4ea] text-[#015e3f] border-[#b7e0c1]',
    gray:   'bg-[#f0ece3] text-[#6a766e] border-[#e3dcd1]',
    amber:  'bg-[#fdf1cf] text-[#8a6d1a] border-[#efe0a3]',
    red:    'bg-[#fde7e7] text-[#9c2626] border-[#f0b7b7]',
    blue:   'bg-[#e6effa] text-[#134a86] border-[#bfd4ef]',
    gold:   'bg-[#fffbf0] text-[#8a6a1c] border-[#eddb9f]',
  };
  return (
    <span data-testid={testid}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10.5px] font-medium tracking-tight ${map[tone]}`}>
      {children}
    </span>
  );
}

function AccountPill({ status, testid }) {
  const m = {
    approved:         { tone: 'green', label: 'Active' },
    inactive:         { tone: 'gray',  label: 'Inactive' },
    pending_approval: { tone: 'amber', label: 'Pending approval' },
    denied:           { tone: 'red',   label: 'Denied' },
    invited:          { tone: 'blue',  label: 'Invited' },
  };
  const cfg = m[status] || { tone: 'gray', label: status || '—' };
  return <Pill tone={cfg.tone} testid={testid}>{cfg.label}</Pill>;
}

function InvitePill({ derived, testid }) {
  const m = {
    used:     { tone: 'green', label: 'Used' },
    pending:  { tone: 'amber', label: 'Pending' },
    expired:  { tone: 'red',   label: 'Expired' },
    canceled: { tone: 'gray',  label: 'Canceled' },
    none:     { tone: 'gray',  label: 'No invite' },
  };
  const cfg = m[derived] || m.none;
  return <Pill tone={cfg.tone} testid={testid}>{cfg.label}</Pill>;
}

function LadderPill({ ladders, testid }) {
  if (!ladders || ladders.length === 0) return <Pill tone="gray" testid={testid}>Not in ladder</Pill>;
  const inS = ladders.find((l) => l.slug === 'singles-ladder');
  const inL = ladders.find((l) => l.slug === 'ladies-singles-ladder');
  return (
    <div className="flex flex-wrap gap-1" data-testid={testid}>
      {inS && <Pill tone="gold">Singles {inS.rank ? `#${inS.rank}` : ''}</Pill>}
      {inL && <Pill tone="gold">Ladies {inL.rank ? `#${inL.rank}` : ''}</Pill>}
    </div>
  );
}

// -------------------- Filters --------------------
const FILTERS = [
  { id: 'all',            label: 'All members' },
  { id: 'needs_attention', label: 'Needs attention' },
  { id: 'likely_test',    label: 'Likely test users' },
  { id: 'never_logged_in',label: 'Never logged in' },
  { id: 'no_activity',    label: 'No club activity' },
  { id: 'active',         label: 'Active' },
  { id: 'inactive',       label: 'Inactive' },
  { id: 'pending_invite', label: 'Pending invite' },
  { id: 'expired_invite', label: 'Expired invite' },
  { id: 'no_invite',      label: 'No invite' },
  { id: 'singles_ladder', label: 'Singles ladder' },
  { id: 'not_in_ladder',  label: 'Not in ladder' },
  { id: 'tournaments',    label: 'Tournament players' },
  { id: 'admins',         label: 'Admins' },
  { id: 'gender_male',    label: 'Gender: Male' },
  { id: 'gender_female',  label: 'Gender: Female' },
  { id: 'gender_unset',   label: 'Gender: Not set' },
];

function memberMatchesFilter(m, filter) {
  switch (filter) {
    case 'all':             return true;
    case 'needs_attention': return (m.invite_status_derived === 'expired')
                                  || (m.invite_status_derived === 'pending')
                                  || (m.account_status === 'pending_approval')
                                  || (!m.last_login && m.invite_status_derived !== 'none');
    case 'likely_test':     return !!m.is_likely_test;
    case 'never_logged_in': return !m.last_login;
    case 'no_activity':     return !!m.no_activity;
    case 'active':          return m.account_status === 'approved';
    case 'inactive':        return m.account_status === 'inactive';
    case 'pending_invite':  return m.invite_status_derived === 'pending';
    case 'expired_invite':  return m.invite_status_derived === 'expired';
    case 'no_invite':       return m.invite_status_derived === 'none';
    case 'singles_ladder':  return m.in_singles;
    case 'not_in_ladder':   return (m.ladders || []).length === 0;
    case 'tournaments':     return (m.tournament_count || 0) > 0;
    case 'admins':          return m.role === 'admin';
    case 'gender_male':     return m.gender === 'male';
    case 'gender_female':   return m.gender === 'female';
    case 'gender_unset':    return !m.gender;
    default:                return true;
  }
}

// -------------------- Formatting --------------------
function shortDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
}

function relTime(iso) {
  if (!iso) return 'Never';
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  if (diff < 0) return shortDate(iso);
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return shortDate(iso);
}

// ==================== Main page ====================
export default function MembersPage() {
  const [members, setMembers] = useState(null);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [openMember, setOpenMember] = useState(null); // profile_id
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDialog, setBulkDialog] = useState(null); // 'deactivate' | 'delete' | 'gender_male' | 'gender_female' | null
  const { user: currentUser, login, enterImpersonation } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'invites' ? 'invites' : 'members';
  const setTab = (id) => setSearchParams(id === 'members' ? {} : { tab: id }, { replace: true });

  const load = useCallback(async () => {
    try {
      const res = await adminAPI.listMembers();
      setMembers(res.data.members || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load members');
      setMembers([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!members) return [];
    const term = q.trim().toLowerCase();
    let list = members.filter((m) => memberMatchesFilter(m, filter));
    if (term) {
      list = list.filter((m) => (
        (m.full_name || '').toLowerCase().includes(term) ||
        (m.email || '').toLowerCase().includes(term) ||
        (m.phone || '').toLowerCase().includes(term)
      ));
    }
    return list;
  }, [members, filter, q]);

  // Prune any selected ids that fell out of the filtered set — keeps the
  // selected count honest with the "operates on filtered result set" rule.
  const visibleSelected = useMemo(() => {
    const vis = new Set(filtered.map((m) => m.profile_id));
    return new Set([...selectedIds].filter((id) => vis.has(id)));
  }, [selectedIds, filtered]);

  const toggleOne = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const selectAllVisible = useCallback(() => {
    setSelectedIds(new Set(filtered.map((m) => m.profile_id)));
  }, [filtered]);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const stats = useMemo(() => {
    if (!members) return null;
    return {
      total:    members.length,
      active:   members.filter((m) => m.account_status === 'approved').length,
      pending:  members.filter((m) => m.invite_status_derived === 'pending').length,
      expired:  members.filter((m) => m.invite_status_derived === 'expired').length,
      singles:  members.filter((m) => m.in_singles).length,
      tourney:  members.filter((m) => (m.tournament_count || 0) > 0).length,
      needsAttn: members.filter((m) => memberMatchesFilter(m, 'needs_attention')).length,
      likelyTest: members.filter((m) => m.is_likely_test).length,
    };
  }, [members]);

  const impersonate = async (m) => {
    try {
      const targetId = m.ladders?.[0]?.player_id || m.profile_id;
      const res = await adminAPI.impersonate(targetId);
      enterImpersonation({ name: m.full_name || 'member', returnTicket: res.data?.return_ticket });
      login(res.data.user, res.data.player, res.data.has_ladder_access, res.data.profile_completed,
            res.data.managed_session_ids, res.data.managed_events);
      toast.success(`Logged in as ${m.full_name}`);
      navigate('/', { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to impersonate');
    }
  };

  const doExport = async () => {
    try {
      const ids = [...visibleSelected];
      const res = await adminAPI.bulkExport(ids);
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `members-export-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${ids.length} member(s).`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    }
  };

  const selectedMembers = useMemo(
    () => (members || []).filter((m) => visibleSelected.has(m.profile_id)),
    [members, visibleSelected]
  );

  return (
    <div className="space-y-6" data-testid="members-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#013523] tracking-tight">Members</h1>
          <p className="text-sm text-[#6a766e] mt-1">
            Everything about every member of Rippers Club — invites, ladders, tournaments, and access.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setTab('invites')}
            data-testid="members-invite-new"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#013523] text-white text-sm rounded-full hover:bg-[#024a30] transition-colors">
            <UserPlus size={16} weight="bold" /> New invite
          </button>
        </div>
      </div>

      {/* Tabs: Members | Invites */}
      <div className="flex gap-1 bg-[#f0ece3] p-1 rounded-xl w-full sm:w-auto sm:inline-flex" data-testid="members-tabs">
        <button data-testid="members-tab-members" onClick={() => setTab('members')}
          className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all ${tab === 'members' ? 'bg-[#fbfaf8] text-[#015e3f] shadow-sm' : 'text-[#6a766e] hover:text-[#013523]'}`}>
          <Users size={14} weight={tab === 'members' ? 'fill' : 'regular'} /> Members
        </button>
        <button data-testid="members-tab-invites" onClick={() => setTab('invites')}
          className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all ${tab === 'invites' ? 'bg-[#fbfaf8] text-[#015e3f] shadow-sm' : 'text-[#6a766e] hover:text-[#013523]'}`}>
          <EnvelopeOpen size={14} weight={tab === 'invites' ? 'fill' : 'regular'} /> Invites
        </button>
      </div>

      {tab === 'invites' && <InvitesPanel />}

      {tab === 'members' && (<>
      {/* Awaiting approval (only renders when there are pending registrations) */}
      <AwaitingApprovalSection onChanged={load} />

      {/* Dashboard cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="members-dashboard">
        <StatCard testid="stat-total"    icon={<Users size={18} weight="duotone" />}         label="Total members" value={stats?.total} tone="green" />
        <StatCard testid="stat-active"   icon={<CheckCircle size={18} weight="duotone" />}   label="Active"        value={stats?.active} tone="green" />
        <StatCard testid="stat-pending"  icon={<PaperPlaneTilt size={18} weight="duotone"/>} label="Invite pending" value={stats?.pending} tone="amber" />
        <StatCard testid="stat-expired"  icon={<Warning size={18} weight="duotone" />}       label="Invite expired" value={stats?.expired} tone="red" highlight={stats?.expired > 0} />
        <StatCard testid="stat-singles"  icon={<IdentificationBadge size={18} weight="duotone"/>} label="Singles ladder" value={stats?.singles} tone="gold" />
        <StatCard testid="stat-tourney"  icon={<Trophy size={18} weight="duotone"/>}         label="Tournament players" value={stats?.tourney} tone="blue" />
      </div>

      {/* Filters + search */}
      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div className="flex gap-1.5 overflow-x-auto scrollbar-thin" data-testid="members-filters">
          {FILTERS.map((f) => {
            const isNeedsAttn = f.id === 'needs_attention';
            const count = isNeedsAttn ? (stats?.needsAttn || 0) : null;
            const active = filter === f.id;
            return (
              <button key={f.id}
                data-testid={`filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                  active
                    ? 'bg-[#013523] text-white border-[#013523]'
                    : 'bg-white text-[#6a766e] border-[#e3dcd1] hover:border-[#013523]/40 hover:text-[#013523]'
                }`}>
                {f.label}
                {isNeedsAttn && count > 0 && (
                  <span className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full text-[10px] font-semibold ${
                    active ? 'bg-white text-[#013523]' : 'bg-[#fde7e7] text-[#9c2626]'
                  }`}>{count}</span>
                )}
              </button>
            );
          })}
        </div>
        <div className="relative shrink-0">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6a766e]" />
          <input
            data-testid="members-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, phone…"
            className="pl-8 pr-3 py-2 text-xs border border-[#e3dcd1] rounded-full w-full md:w-72 focus:outline-none focus:border-[#013523]"
          />
        </div>
      </div>

      {/* Bulk action toolbar (only visible when at least 1 selected) */}
      {visibleSelected.size > 0 && (
        <div className="flex items-center justify-between gap-3 bg-[#013523] text-white rounded-full px-4 py-2 flex-wrap"
             data-testid="bulk-toolbar">
          <div className="flex items-center gap-3 text-[13px]">
            <span data-testid="bulk-selected-count" className="font-medium">
              {visibleSelected.size} selected
            </span>
            <button onClick={clearSelection} data-testid="bulk-clear"
                    className="text-[11.5px] text-white/70 hover:text-white underline">
              Clear selection
            </button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={doExport} data-testid="bulk-export"
                    className="text-[12px] px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20">
              Export CSV
            </button>
            <button onClick={() => setBulkDialog('gender_male')} data-testid="bulk-set-male"
                    className="text-[12px] px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20">
              Set Male
            </button>
            <button onClick={() => setBulkDialog('gender_female')} data-testid="bulk-set-female"
                    className="text-[12px] px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20">
              Set Female
            </button>
            <button onClick={() => setBulkDialog('deactivate')} data-testid="bulk-deactivate"
                    className="text-[12px] px-3 py-1.5 rounded-full bg-[#c9a93a] text-[#013523] hover:bg-[#daa64a]">
              Deactivate selected
            </button>
            <button onClick={() => setBulkDialog('delete')} data-testid="bulk-delete"
                    className="text-[12px] px-3 py-1.5 rounded-full bg-[#9c2626] text-white hover:bg-[#7a1d1d]">
              Delete selected…
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-[#e3dcd1] rounded-2xl overflow-hidden" data-testid="members-table-wrapper">
        {members === null ? (
          <div className="p-8 text-center text-sm text-[#6a766e]">Loading members…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#6a766e]">No members match this filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="members-table">
              <thead className="bg-[#fbfaf8] border-b border-[#e3dcd1]">
                <tr className="text-left text-[11px] font-medium text-[#6a766e] uppercase tracking-wider">
                  <th className="px-3 py-3 w-8">
                    <input type="checkbox"
                      data-testid="select-all-visible"
                      checked={filtered.length > 0 && visibleSelected.size === filtered.length}
                      ref={(el) => { if (el) el.indeterminate = visibleSelected.size > 0 && visibleSelected.size < filtered.length; }}
                      onChange={(e) => e.target.checked ? selectAllVisible() : clearSelection()}
                      className="w-4 h-4 rounded border-[#e3dcd1] text-[#015e3f] focus:ring-[#015e3f]/20 cursor-pointer" />
                  </th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3 hidden md:table-cell">Contact</th>
                  <th className="px-4 py-3">Account</th>
                  <th className="px-4 py-3">Invite</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Ladder</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Last login</th>
                  <th className="px-4 py-3 text-right sticky right-0 z-20 bg-[#fbfaf8] shadow-[-8px_0_8px_-6px_rgba(0,0,0,0.06)]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => (
                  <MemberRow key={m.profile_id} m={m}
                             isSelected={visibleSelected.has(m.profile_id)}
                             isCurrentUser={m.profile_id === currentUser?.id}
                             onToggleSelect={() => toggleOne(m.profile_id)}
                             onOpen={() => setOpenMember(m.profile_id)}
                             onImpersonate={() => impersonate(m)}
                             onReload={load} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-4 py-2.5 text-[11px] text-[#6a766e] border-t border-[#e3dcd1] bg-[#fbfaf8]">
          {members === null ? '' : `${filtered.length} of ${members.length} members${visibleSelected.size ? ` · ${visibleSelected.size} selected` : ''}`}
        </div>
      </div>
      </>)}

      {/* Drawer */}
      {openMember && (
        <MemberDrawer profileId={openMember}
                      onClose={() => setOpenMember(null)}
                      onImpersonate={impersonate}
                      onReload={load} />
      )}

      {/* Bulk dialogs */}
      {bulkDialog === 'deactivate' && (
        <BulkDeactivateDialog
          selected={selectedMembers}
          currentUserId={currentUser?.id}
          onClose={() => setBulkDialog(null)}
          onDone={async () => {
            setBulkDialog(null);
            setSelectedIds(new Set());
            await load();
          }}
        />
      )}
      {bulkDialog === 'delete' && (
        <BulkDeleteDialog
          selected={selectedMembers}
          onClose={() => setBulkDialog(null)}
          onDone={async () => {
            setBulkDialog(null);
            setSelectedIds(new Set());
            await load();
          }}
        />
      )}
      {(bulkDialog === 'gender_male' || bulkDialog === 'gender_female') && (
        <BulkGenderDialog
          selected={selectedMembers}
          gender={bulkDialog === 'gender_male' ? 'male' : 'female'}
          onClose={() => setBulkDialog(null)}
          onDone={async () => {
            setBulkDialog(null);
            setSelectedIds(new Set());
            await load();
          }}
        />
      )}
    </div>
  );
}

function StatCard({ icon, label, value, tone = 'green', testid, highlight = false }) {
  const toneMap = {
    green: 'text-[#015e3f]',
    amber: 'text-[#8a6d1a]',
    red:   'text-[#9c2626]',
    gold:  'text-[#c9a93a]',
    blue:  'text-[#134a86]',
  };
  return (
    <div data-testid={testid}
      className={`bg-white border rounded-2xl p-3 flex items-center gap-3 transition-shadow hover:shadow-sm ${
        highlight ? 'border-[#f0b7b7] bg-[#fdf5f5]' : 'border-[#e3dcd1]'
      }`}>
      <div className={`w-9 h-9 rounded-xl bg-[#f0ece3] flex items-center justify-center ${toneMap[tone]}`}>
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-[10.5px] text-[#6a766e] uppercase tracking-wider">{label}</div>
        <div className={`font-serif text-2xl leading-tight ${highlight ? 'text-[#9c2626]' : 'text-[#013523]'}`}>
          {value === undefined ? '—' : value}
        </div>
      </div>
    </div>
  );
}

// ==================== Row ====================
function MemberRow({ m, onOpen, onImpersonate, onReload, isSelected, isCurrentUser, onToggleSelect }) {
  const [menu, setMenu] = useState(false);
  return (
    <tr
      data-testid={`member-row-${m.profile_id}`}
      className={`group border-b border-[#f0ece3] last:border-b-0 transition-colors cursor-pointer ${
        isSelected ? 'bg-[#f0f7f2]' : 'hover:bg-[#fbfaf8]'
      }`}
    >
      <td className="px-3 py-3 w-8" onClick={(e) => e.stopPropagation()}>
        <input type="checkbox"
          data-testid={`select-${m.profile_id}`}
          checked={!!isSelected}
          onChange={onToggleSelect}
          disabled={isCurrentUser}
          title={isCurrentUser ? "You can't select your own account for bulk actions" : undefined}
          className="w-4 h-4 rounded border-[#e3dcd1] text-[#015e3f] focus:ring-[#015e3f]/20 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed" />
      </td>
      <td className="px-4 py-3" onClick={onOpen}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[#f0ece3] text-[#013523] flex items-center justify-center text-[11px] font-semibold shrink-0">
            {(m.first_name?.[0] || m.email?.[0] || '?').toUpperCase()}{(m.last_name?.[0] || '').toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-[13px] font-medium text-[#013523] truncate">{m.full_name}</div>
            {m.role === 'admin' && <div className="text-[10px] text-[#c9a93a] uppercase tracking-wider">Admin</div>}
            {m.is_likely_test && m.test_signals?.length > 0 && (
              <div className="text-[10px] text-[#8a6a1c]" title={m.test_signals.join(' · ')}>
                Likely test · {m.test_signals[0]}
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3 hidden md:table-cell text-[#6a766e] text-xs" onClick={onOpen}>
        <div className="truncate max-w-[220px]">{m.email || '—'}</div>
        {m.phone && <div className="text-[11px]">{m.phone}</div>}
      </td>
      <td className="px-4 py-3" onClick={onOpen}>
        <AccountPill status={m.account_status} testid={`pill-account-${m.profile_id}`} />
      </td>
      <td className="px-4 py-3" onClick={onOpen}>
        <InvitePill derived={m.invite_status_derived} testid={`pill-invite-${m.profile_id}`} />
      </td>
      <td className="px-4 py-3 hidden lg:table-cell" onClick={onOpen}>
        <LadderPill ladders={m.ladders} testid={`pill-ladder-${m.profile_id}`} />
      </td>
      <td className="px-4 py-3 hidden lg:table-cell text-[11px] text-[#6a766e]" onClick={onOpen}>
        {relTime(m.last_login)}
      </td>
      <td className={`px-4 py-3 text-right relative sticky right-0 z-10 shadow-[-8px_0_8px_-6px_rgba(0,0,0,0.06)] ${isSelected ? 'bg-[#f0f7f2]' : 'bg-white group-hover:bg-[#fbfaf8]'}`} onClick={(e) => e.stopPropagation()}>
        <div className="inline-flex items-center gap-1">
          <button onClick={onOpen}
            data-testid={`member-manage-${m.profile_id}`}
            className="text-[11px] px-3 py-1.5 rounded-full bg-[#013523] text-white hover:bg-[#024a30]">
            Manage
          </button>
          <button onClick={() => setMenu(!menu)}
            data-testid={`member-menu-${m.profile_id}`}
            className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-[#f0ece3] text-[#6a766e]">
            <DotsThreeVertical size={16} weight="bold" />
          </button>
        </div>
        {menu && (
          <RowMenu m={m} onClose={() => setMenu(false)}
                   onImpersonate={() => { setMenu(false); onImpersonate(); }}
                   onReload={onReload} />
        )}
      </td>
    </tr>
  );
}

function RowMenu({ m, onClose, onImpersonate, onReload }) {
  const [busy, setBusy] = useState(null);

  const call = async (label, fn) => {
    setBusy(label);
    try {
      const result = await fn();
      return result;
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed: ${label}`);
    } finally {
      setBusy(null);
    }
  };

  const regen = async () => {
    const r = await call('regenerate', () => adminAPI.regenerateInvite({ profile_id: m.profile_id, send_method: 'none' }));
    if (r?.data?.invite_url) {
      try {
        await navigator.clipboard.writeText(r.data.invite_url);
        toast.success('New invite generated & link copied to clipboard.');
      } catch {
        toast.success(`New invite: ${r.data.invite_url}`);
      }
      onReload?.();
      onClose();
    }
  };

  const regenSms = async () => {
    const r = await call('regenerate+sms', () => adminAPI.regenerateInvite({ profile_id: m.profile_id, send_method: 'sms' }));
    if (r?.data) {
      const s = r.data.dispatch?.sms;
      if (s?.status === 'sent' || s?.status === 'dry_run' || s?.status === 'off') {
        toast.success(`New invite generated & SMS ${s.status} to ${s.to}`);
      } else {
        toast.warning(`Invite generated but SMS: ${s?.reason || s?.status || 'unknown'}`);
      }
      onReload?.();
      onClose();
    }
  };

  const resend = async () => {
    if (!m.invite?.id) { toast.error('No pending invite to resend.'); return; }
    const r = await call('resend', () => adminAPI.resendInvite(m.invite.id, 'sms'));
    if (r?.data) {
      const s = r.data.dispatch?.sms;
      if (s?.status === 'sent' || s?.status === 'dry_run' || s?.status === 'off') {
        toast.success(`SMS ${s.status} to ${s.to}`);
      } else {
        toast.warning(`SMS: ${s?.reason || s?.status || 'unknown'}`);
      }
      onClose();
    }
  };

  const copyLink = async () => {
    if (!m.invite?.code) { toast.error('No invite to copy.'); return; }
    const url = `${window.location.origin}/onboard?code=${m.invite.code}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Invite link copied');
    } catch {
      toast.error('Could not copy — copy manually: ' + url);
    }
    onClose();
  };

  const addToSingles = async () => {
    await call('add-to-ladder', async () => {
      const r = await adminAPI.addPlayerToLadder(m.profile_id, 'singles-ladder');
      toast.success(`${m.full_name} added to Singles Ladder${r.data?.rank ? ` @ #${r.data.rank}` : ''}`);
      onReload?.();
      onClose();
    });
  };

  const setStatus = async (status) => {
    await call('status', async () => {
      await adminAPI.setMemberStatus(m.profile_id, status);
      toast.success(status === 'inactive' ? 'Member deactivated' : 'Member reactivated');
      onReload?.();
      onClose();
    });
  };

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div
        data-testid={`member-menu-panel-${m.profile_id}`}
        className="absolute right-4 top-11 z-50 w-64 bg-white border border-[#e3dcd1] rounded-xl shadow-lg overflow-hidden text-left">
        <MenuItem icon={<UserSwitch size={14} weight="bold" />} label="Login as user (impersonate)"
                  testid={`menu-impersonate-${m.profile_id}`} onClick={onImpersonate} />
        {!m.in_singles && (
          <MenuItem icon={<IdentificationBadge size={14} weight="bold" />} label="Add to Singles Ladder"
                    testid={`menu-add-singles-${m.profile_id}`} onClick={addToSingles} disabled={busy === 'add-to-ladder'} />
        )}
        <MenuDivider />
        <MenuItem icon={<ArrowClockwise size={14} weight="bold" />} label="Regenerate invite (copy link)"
                  testid={`menu-regen-${m.profile_id}`} onClick={regen} disabled={busy?.startsWith('regenerate')} />
        {m.phone && (
          <MenuItem icon={<PaperPlaneTilt size={14} weight="bold" />} label="Regenerate & SMS"
                    testid={`menu-regen-sms-${m.profile_id}`} onClick={regenSms} disabled={busy?.startsWith('regenerate')} />
        )}
        {m.invite?.status === 'pending' && (
          <MenuItem icon={<PaperPlaneTilt size={14} weight="bold" />} label="Resend invite (SMS)"
                    testid={`menu-resend-${m.profile_id}`} onClick={resend} disabled={busy === 'resend'} />
        )}
        {m.invite?.code && (
          <MenuItem icon={<Copy size={14} weight="bold" />} label="Copy invite link"
                    testid={`menu-copy-${m.profile_id}`} onClick={copyLink} />
        )}
        <MenuDivider />
        {m.account_status === 'inactive' ? (
          <MenuItem icon={<CheckCircle size={14} weight="bold" />} label="Reactivate member"
                    testid={`menu-reactivate-${m.profile_id}`} onClick={() => setStatus('approved')} />
        ) : (
          <MenuItem icon={<Prohibit size={14} weight="bold" />} label="Deactivate member" tone="red"
                    testid={`menu-deactivate-${m.profile_id}`} onClick={() => setStatus('inactive')} />
        )}
      </div>
    </>
  );
}

function MenuItem({ icon, label, onClick, disabled, testid, tone = 'default' }) {
  const colors = tone === 'red' ? 'text-[#9c2626] hover:bg-[#fde7e7]' : 'text-[#013523] hover:bg-[#f0ece3]';
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
      className={`w-full flex items-center gap-2 px-3 py-2 text-[12px] ${colors} disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}>
      <span className="text-[#6a766e]">{icon}</span>
      <span className="truncate">{label}</span>
    </button>
  );
}
function MenuDivider() { return <div className="h-px bg-[#e3dcd1]" />; }

// ==================== Drawer ====================
function MemberDrawer({ profileId, onClose, onImpersonate, onReload }) {
  const [detail, setDetail] = useState(null);
  const [confirmRemoveLadder, setConfirmRemoveLadder] = useState(null); // {playerId, ladderName}
  const [removingLadder, setRemovingLadder] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await adminAPI.getMember(profileId);
      setDetail(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load member');
    }
  }, [profileId]);
  useEffect(() => { load(); }, [load]);

  if (!detail) {
    return (
      <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex justify-end"
           data-testid="member-drawer-loading" onClick={onClose}>
        <div className="w-full max-w-lg bg-white h-full p-6 text-sm text-[#6a766e]">Loading…</div>
      </div>
    );
  }

  const p = detail.profile;
  const inviteRow = detail.invites?.[0];
  const inviteStatus = inviteRow
    ? (inviteRow.status === 'pending' && inviteRow.expires_at && inviteRow.expires_at < new Date().toISOString()
        ? 'expired' : inviteRow.status)
    : 'none';

  const copyLink = async () => {
    if (!inviteRow?.invite_code) { toast.error('No invite to copy.'); return; }
    const url = `${window.location.origin}/onboard?code=${inviteRow.invite_code}`;
    try { await navigator.clipboard.writeText(url); toast.success('Invite link copied'); }
    catch { toast.error('Copy failed — copy manually: ' + url); }
  };
  const regen = async (send) => {
    try {
      const r = await adminAPI.regenerateInvite({ profile_id: p.id, send_method: send });
      if (r.data?.invite_url) {
        if (send === 'none') {
          try { await navigator.clipboard.writeText(r.data.invite_url); } catch { /* ignore */ }
          toast.success('New invite generated & link copied');
        } else {
          const s = r.data.dispatch?.sms;
          toast.success(`New invite generated & SMS ${s?.status}`);
        }
        await load();
        onReload?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Regenerate failed');
    }
  };
  const impersonate = () => onImpersonate({
    profile_id: p.id,
    full_name: p.full_name || p.email,
    ladders: detail.ladders,
  });
  const addSingles = async () => {
    try {
      await adminAPI.addPlayerToLadder(p.id, 'singles-ladder');
      toast.success('Added to Singles Ladder');
      await load(); onReload?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Add failed'); }
  };
  const addLadies = async () => {
    try {
      await adminAPI.addPlayerToLadder(p.id, 'ladies-singles-ladder');
      toast.success('Added to Ladies Singles Ladder');
      await load(); onReload?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Add failed'); }
  };
  const removeFromLadder = async () => {
    if (!confirmRemoveLadder) return;
    const { playerId, ladderName } = confirmRemoveLadder;
    setRemovingLadder(true);
    try {
      await adminAPI.removePlayer(playerId);
      toast.success(`Removed from ${ladderName}`);
      setConfirmRemoveLadder(null);
      await load(); onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Remove failed');
    } finally {
      setRemovingLadder(false);
    }
  };
  const setStatus = async (status) => {
    try {
      await adminAPI.setMemberStatus(p.id, status);
      toast.success(status === 'inactive' ? 'Member deactivated' : 'Member reactivated');
      await load(); onReload?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const inSingles = (detail.ladders || []).some((l) => l.slug === 'singles-ladder');
  const inLadies  = (detail.ladders || []).some((l) => l.slug === 'ladies-singles-ladder');
  const ladiesEligible = !p.gender || p.gender === 'female' || p.gender === 'Female' || p.gender === 'F';

  return (
    <div
      data-testid="member-drawer"
      className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex justify-end"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg h-full bg-[#fbfaf8] overflow-y-auto shadow-2xl border-l border-[#e3dcd1]"
        onClick={(e) => e.stopPropagation()}
        data-testid="member-drawer-content"
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#fbfaf8]/95 backdrop-blur border-b border-[#e3dcd1] px-6 py-4 flex items-start justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-full bg-[#f0ece3] text-[#013523] flex items-center justify-center text-sm font-semibold">
              {(p.first_name?.[0] || p.email?.[0] || '?').toUpperCase()}{(p.last_name?.[0] || '').toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="font-serif text-xl text-[#013523] truncate" data-testid="drawer-name">{p.full_name}</div>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                <AccountPill status={p.account_status} testid="drawer-account-pill" />
                <InvitePill derived={inviteStatus === 'redeemed' ? 'used' : inviteStatus === 'none' ? 'none' : inviteStatus} testid="drawer-invite-pill" />
                {p.role === 'admin' && <Pill tone="gold">Admin</Pill>}
              </div>
            </div>
          </div>
          <button onClick={onClose} data-testid="drawer-close"
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#f0ece3] text-[#6a766e]">
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Account & Contact — editable (admin) */}
          <MemberAccountEditor
            p={p}
            account={detail.account || {}}
            onSaved={async () => { await load(); onReload?.(); }}
          />
          <Section title="More">
            <Field label="Rating" value={p.usta_rating || '—'} />
            <Field label="Created" value={shortDate(p.created_at)} />
            <Field label="Last login" value={relTime(detail.last_login)} />
            <Field
              label="SMS"
              value={
                <span data-testid="drawer-sms-status" className={detail.account?.sms_suppressed ? 'text-[#b4532a] font-medium' : (detail.account?.sms_opt_in ? 'text-[#015e3f]' : 'text-[#6a766e]')}>
                  {detail.account?.sms_suppressed ? 'Opted Out' : (detail.account?.sms_opt_in ? 'Yes' : 'No')}
                </span>
              }
            />
          </Section>

          {/* Ladders — Phase L-ADMIN1: per-row Remove + Open Manage shortcuts */}
          <Section title="Ladders">
            {detail.ladders?.length ? (
              <div className="space-y-1.5" data-testid="drawer-ladders">
                {detail.ladders.map((l) => (
                  <div key={l.player_id} className="flex items-center justify-between bg-white border border-[#e3dcd1] rounded-xl px-3 py-2">
                    <div className="min-w-0">
                      <div className="text-sm text-[#013523]">{l.name}</div>
                      <div className="text-[11px] text-[#6a766e]">{l.status}{l.rank ? ` · Rank #${l.rank}` : ''}</div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Pill tone="gold">In</Pill>
                      <a href={`/admin/ladders/${l.slug}/manage`}
                         data-testid={`drawer-open-ladder-${l.slug}`}
                         className="text-[10.5px] text-[#015e3f] hover:underline inline-flex items-center gap-0.5">
                         Manage <ArrowSquareOut size={10} />
                      </a>
                      <button onClick={() => setConfirmRemoveLadder({ playerId: l.player_id, ladderName: l.name })}
                              data-testid={`drawer-remove-ladder-${l.slug}`}
                              className="text-[10.5px] text-[#9c2626] hover:underline">
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyRow msg="Not in any ladder" />
            )}
          </Section>

          {/* Tournaments */}
          <Section title="Tournaments">
            {detail.tournaments?.length ? (
              <div className="space-y-1.5" data-testid="drawer-tournaments">
                {detail.tournaments.map((t) => (
                  <div key={t.tournament_id} className="flex items-center justify-between bg-white border border-[#e3dcd1] rounded-xl px-3 py-2">
                    <div>
                      <div className="text-sm text-[#013523]">{t.name}</div>
                      <div className="text-[11px] text-[#6a766e]">{t.tournament_type} · {t.status}</div>
                    </div>
                    <a href={`/tournaments/${t.tournament_id}`} className="text-[#015e3f] text-[11px] inline-flex items-center gap-1">
                      Open <ArrowSquareOut size={11} />
                    </a>
                  </div>
                ))}
              </div>
            ) : <EmptyRow msg="No tournament entries" />}
          </Section>

          {/* Round robins */}
          <Section title="Round robins">
            {detail.round_robins?.length ? (
              <div className="space-y-1.5" data-testid="drawer-round-robins">
                {detail.round_robins.map((r) => (
                  <div key={r.round_robin_id} className="flex items-center justify-between bg-white border border-[#e3dcd1] rounded-xl px-3 py-2">
                    <div>
                      <div className="text-sm text-[#013523]">{r.name}</div>
                      <div className="text-[11px] text-[#6a766e]">{r.rr_type} · {r.status}</div>
                    </div>
                    <a href={`/round-robins/${r.round_robin_id}`} className="text-[#015e3f] text-[11px] inline-flex items-center gap-1">
                      Open <ArrowSquareOut size={11} />
                    </a>
                  </div>
                ))}
              </div>
            ) : <EmptyRow msg="No round robin entries" />}
          </Section>

          {/* Invite history */}
          <Section title="Invite history">
            {detail.invites?.length ? (
              <div className="space-y-1.5" data-testid="drawer-invites">
                {detail.invites.map((inv, idx) => {
                  const isLatest = idx === 0;
                  const st = inv.status === 'pending' && inv.expires_at && inv.expires_at < new Date().toISOString()
                    ? 'expired' : inv.status;
                  return (
                    <div key={inv.id} className="bg-white border border-[#e3dcd1] rounded-xl px-3 py-2 flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="text-[13px] font-mono text-[#013523]">{inv.invite_code}</div>
                        <div className="text-[10.5px] text-[#6a766e]">
                          Created {shortDate(inv.created_at)}
                          {inv.expires_at && ` · Expires ${shortDate(inv.expires_at)}`}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <InvitePill derived={st === 'redeemed' ? 'used' : st} />
                        {isLatest && <span className="text-[10px] text-[#6a766e]">Latest</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <EmptyRow msg="No invites on file" />}
          </Section>

          {/* Club history placeholder */}
          <Section title="Club history">
            <EmptyRow msg="Match & event history coming soon" />
          </Section>
        </div>

        {/* Sticky action bar — 2-column grid so all 6 actions fit within max-w-lg drawer */}
        <div className="sticky bottom-0 bg-[#fbfaf8]/95 backdrop-blur border-t border-[#e3dcd1] px-6 py-3 grid grid-cols-2 gap-2" data-testid="drawer-actions">
          <button onClick={impersonate} data-testid="drawer-impersonate"
                  className="col-span-2 inline-flex items-center justify-center gap-1.5 text-[12px] px-3 py-2 rounded-full bg-[#013523] text-white hover:bg-[#024a30]">
            <UserSwitch size={14} weight="bold" /> Impersonate
          </button>
          {!inSingles && (
            <button onClick={addSingles} data-testid="drawer-add-singles"
                    className="col-span-2 inline-flex items-center justify-center gap-1.5 text-[12px] px-3 py-2 rounded-full bg-white border border-[#e3dcd1] text-[#013523] hover:border-[#013523]/40">
              <IdentificationBadge size={14} weight="bold" /> Add to Singles Ladder
            </button>
          )}
          {!inLadies && ladiesEligible && (
            <button onClick={addLadies} data-testid="drawer-add-ladies"
                    className="col-span-2 inline-flex items-center justify-center gap-1.5 text-[12px] px-3 py-2 rounded-full bg-white border border-[#e3dcd1] text-[#013523] hover:border-[#013523]/40">
              <IdentificationBadge size={14} weight="bold" /> Add to Ladies Singles Ladder
            </button>
          )}
          {(inSingles || inLadies) && (
            <a href={`/admin/ladders/${inLadies && !inSingles ? 'ladies-singles-ladder' : 'singles-ladder'}/manage`}
               data-testid="drawer-open-ladder-management"
               className="col-span-2 inline-flex items-center justify-center gap-1.5 text-[12px] px-3 py-2 rounded-full bg-white border border-[#e3dcd1] text-[#013523] hover:border-[#013523]/40">
              <Trophy size={14} weight="bold" /> Open Ladder Management
            </a>
          )}
          <button onClick={() => regen('none')} data-testid="drawer-regen"
                  className="inline-flex items-center justify-center gap-1.5 text-[12px] px-3 py-2 rounded-full bg-white border border-[#e3dcd1] text-[#013523] hover:border-[#013523]/40">
            <ArrowClockwise size={14} weight="bold" /> Regenerate
          </button>
          {p.phone ? (
            <button onClick={() => regen('sms')} data-testid="drawer-regen-sms"
                    className="inline-flex items-center justify-center gap-1.5 text-[12px] px-3 py-2 rounded-full bg-white border border-[#e3dcd1] text-[#013523] hover:border-[#013523]/40">
              <PaperPlaneTilt size={14} weight="bold" /> Regen + SMS
            </button>
          ) : <div />}
          {inviteRow?.invite_code && (
            <button onClick={copyLink} data-testid="drawer-copy"
                    className="col-span-2 inline-flex items-center justify-center gap-1.5 text-[12px] px-3 py-2 rounded-full bg-white border border-[#e3dcd1] text-[#013523] hover:border-[#013523]/40">
              <Copy size={14} weight="bold" /> Copy invite link
            </button>
          )}
          {p.account_status === 'inactive' ? (
            <button onClick={() => setStatus('approved')} data-testid="drawer-reactivate"
                    className="col-span-2 inline-flex items-center justify-center gap-1.5 text-[12px] px-3 py-2 rounded-full bg-white border border-[#e3dcd1] text-[#013523] hover:border-[#013523]/40">
              <CheckCircle size={14} weight="bold" /> Reactivate member
            </button>
          ) : (
            <button onClick={() => setStatus('inactive')} data-testid="drawer-deactivate"
                    className="col-span-2 inline-flex items-center justify-center gap-1.5 text-[12px] px-3 py-2 rounded-full bg-white border border-[#f0b7b7] text-[#9c2626] hover:bg-[#fde7e7]">
              <Prohibit size={14} weight="bold" /> Deactivate member
            </button>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={!!confirmRemoveLadder}
        onOpenChange={(v) => !v && setConfirmRemoveLadder(null)}
        title="Remove from ladder?"
        description={confirmRemoveLadder
          ? `Remove ${p.full_name} from ${confirmRemoveLadder.ladderName}? Their profile and history stay intact.`
          : ''}
        confirmLabel="Remove"
        destructive
        busy={removingLadder}
        onConfirm={removeFromLadder}
      />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-[10.5px] text-[#6a766e] uppercase tracking-widest mb-2">{title}</h3>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function Field({ label, value }) {
  return (
    <div className="flex items-center justify-between text-[12.5px] bg-white border border-[#e3dcd1] rounded-xl px-3 py-2">
      <span className="text-[#6a766e]">{label}</span>
      <span className="text-[#013523] font-medium truncate ml-3">{value || '—'}</span>
    </div>
  );
}
function EmptyRow({ msg }) {
  return <div className="text-[11.5px] text-[#6a766e] italic px-3 py-2">{msg}</div>;
}

// ==================== Member Account Editor (admin) ====================
function MemberAccountEditor({ p, account, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [form, setForm] = useState({
    first_name: p.first_name || '',
    last_name: p.last_name || '',
    email: account.has_real_email ? (p.email || '') : '',
    phone: p.phone || '',
    gender: p.gender || '',
  });

  const claimed = !!account.claimed;
  const hasRealEmail = !!account.has_real_email;

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        first_name: form.first_name,
        last_name: form.last_name,
        phone: form.phone,
      };
      if (form.gender) payload.gender = form.gender;
      // Only send email when it's a real, changed value and the account is unclaimed.
      if (!claimed && form.email && form.email.trim().toLowerCase() !== (p.email || '').toLowerCase()) {
        payload.email = form.email.trim();
      }
      await adminAPI.editMember(p.id, payload);
      toast.success('Member updated');
      setEditing(false);
      await onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setBusy(false); }
  };

  const sendInvite = async (method) => {
    setInviteBusy(true);
    try {
      const r = await adminAPI.sendMemberInvite(p.id, method);
      const d = r.data?.dispatch || {};
      if (method === 'sms') {
        if (d.status === 'sent') toast.success(`SMS invitation sent to ${d.to}`);
        else if (d.status === 'dry_run') toast.success('SMS invite prepared (dry-run mode — not actually sent)');
        else toast.error(`Invitation could not be sent: ${d.reason || d.status}`);
      } else {
        if (d.claim_url) {
          try { await navigator.clipboard.writeText(d.claim_url); } catch { /* ignore */ }
          toast.success('Email provider not set up — invite link copied to clipboard');
        } else toast.error(`Invitation could not be sent: ${d.reason || d.status}`);
      }
      await onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invitation could not be sent');
    } finally { setInviteBusy(false); }
  };

  return (
    <div data-testid="member-account-section">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-[10.5px] text-[#6a766e] uppercase tracking-widest">Account & Contact</h3>
        {!editing && (
          <button data-testid="member-edit-toggle" onClick={() => setEditing(true)}
                  className="text-[11px] text-[#015e3f] hover:underline">Edit</button>
        )}
      </div>

      {/* Account status line */}
      <div className="flex items-center gap-2 mb-2 flex-wrap text-[11.5px]" data-testid="member-account-status">
        <Pill tone={claimed ? 'green' : 'gray'}>{claimed ? 'Claimed' : 'Never logged in'}</Pill>
        {account.last_invite_at && <span className="text-[#6a766e]">Last invite {relTime(account.last_invite_at)}</span>}
      </div>

      {!editing ? (
        <div className="space-y-1">
          <Field label="First name" value={p.first_name} />
          <Field label="Last name" value={p.last_name} />
          <Field label="Email" value={hasRealEmail ? p.email : 'No real email yet'} />
          <Field label="Phone" value={p.phone} />
          <Field label="Gender" value={p.gender || 'Not set'} />
        </div>
      ) : (
        <div className="space-y-2" data-testid="member-edit-form">
          <EditInput label="First name" testid="member-edit-first" value={form.first_name}
                     onChange={(v) => setForm({ ...form, first_name: v })} />
          <EditInput label="Last name" testid="member-edit-last" value={form.last_name}
                     onChange={(v) => setForm({ ...form, last_name: v })} />
          <div>
            <EditInput label="Email" testid="member-edit-email" value={form.email} disabled={claimed}
                       onChange={(v) => setForm({ ...form, email: v })} />
            {claimed && <div className="text-[10.5px] text-[#9c7a1a] mt-0.5 px-1">Login email is locked — this member has already logged in.</div>}
          </div>
          <EditInput label="Phone" testid="member-edit-phone" value={form.phone}
                     onChange={(v) => setForm({ ...form, phone: v })} />
          <div className="flex items-center justify-between text-[12.5px] bg-white border border-[#e3dcd1] rounded-xl px-3 py-2">
            <span className="text-[#6a766e]">Gender</span>
            <select data-testid="member-edit-gender" value={form.gender}
                    onChange={(e) => setForm({ ...form, gender: e.target.value })}
                    className="text-[#013523] font-medium bg-transparent outline-none">
              <option value="">Not set</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
          <div className="flex gap-2 pt-1">
            <button data-testid="member-edit-cancel" onClick={() => { setEditing(false); setForm({
              first_name: p.first_name || '', last_name: p.last_name || '',
              email: hasRealEmail ? (p.email || '') : '', phone: p.phone || '', gender: p.gender || '' }); }}
              className="text-[12px] text-[#013523] px-3 py-1.5 rounded-full border border-[#e3dcd1] hover:bg-white">Cancel</button>
            <button data-testid="member-edit-save" onClick={save} disabled={busy}
              className={`text-[12px] rounded-full px-4 py-1.5 ${busy ? 'bg-[#a0aaa4] text-white' : 'bg-[#015e3f] hover:bg-[#014d34] text-white'}`}>
              {busy ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      )}

      {/* Invite controls — only for members who have not logged in */}
      {!claimed && (
        <div className="mt-3 bg-white border border-[#e3dcd1] rounded-xl px-3 py-2.5" data-testid="member-invite-controls">
          <div className="text-[11px] text-[#6a766e] mb-2">Send / resend account invite</div>
          <div className="flex gap-2">
            <button data-testid="member-invite-sms" onClick={() => sendInvite('sms')} disabled={inviteBusy}
              className="text-[12px] rounded-full px-3 py-1.5 bg-[#013523] text-white disabled:opacity-60">
              {inviteBusy ? 'Sending…' : 'Send SMS invite'}</button>
            <button data-testid="member-invite-email" onClick={() => sendInvite('email')} disabled={inviteBusy}
              className="text-[12px] rounded-full px-3 py-1.5 border border-[#e3dcd1] text-[#013523] hover:bg-[#fbfaf8] disabled:opacity-60">
              Email invite</button>
          </div>
          {!hasRealEmail && <div className="text-[10.5px] text-[#9c7a1a] mt-2">Add a real email above before inviting (needed so they can log in).</div>}
        </div>
      )}
    </div>
  );
}

function EditInput({ label, value, onChange, testid, disabled = false }) {
  return (
    <div className="flex items-center justify-between text-[12.5px] bg-white border border-[#e3dcd1] rounded-xl px-3 py-2">
      <span className="text-[#6a766e] shrink-0 mr-3">{label}</span>
      <input data-testid={testid} value={value} disabled={disabled}
             onChange={(e) => onChange(e.target.value)}
             className="text-[#013523] font-medium bg-transparent outline-none text-right flex-1 disabled:text-[#a0aaa4]" />
    </div>
  );
}


// ==================== Bulk Deactivate Dialog ====================
function BulkDeactivateDialog({ selected, currentUserId, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  // Guardrail: filter out current admin so the API 400 never triggers.
  const eligible = selected.filter((m) => m.profile_id !== currentUserId);
  const inLadders   = eligible.filter((m) => (m.ladders || []).length > 0).length;
  const inTourneys  = eligible.filter((m) => (m.tournament_count || 0) > 0
                                          || (m.round_robin_count || 0) > 0).length;
  const isAdmin     = eligible.filter((m) => m.role === 'admin').length;

  const doIt = async () => {
    setBusy(true);
    try {
      const ids = eligible.map((m) => m.profile_id);
      const r = await adminAPI.bulkDeactivate(ids);
      toast.success(`Deactivated ${r.data.count} member(s).`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Deactivate failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="bulk-deactivate-dialog">
        <DialogHeader>
          <DialogTitle>Deactivate {eligible.length} member{eligible.length === 1 ? '' : 's'}?</DialogTitle>
        </DialogHeader>
        <div className="px-5 pb-4 sm:px-6 text-[13px] text-[#2a332c] space-y-2">
          <p>They&apos;ll be removed from the member-facing Directory and blocked from signing in. All match, ladder, tournament and round-robin history is preserved.</p>
          <ul className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-lg p-3 space-y-1 text-[12.5px]">
            <li>· <b>{eligible.length}</b> account{eligible.length === 1 ? '' : 's'} selected</li>
            {inLadders > 0 && <li className="text-[#8a6d1a]">· {inLadders} currently in a ladder</li>}
            {inTourneys > 0 && <li className="text-[#8a6d1a]">· {inTourneys} in tournaments / round robins</li>}
            {isAdmin > 0 && <li className="text-[#9c2626]">· {isAdmin} admin account{isAdmin === 1 ? '' : 's'} — admins retain access until you demote them separately</li>}
          </ul>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="deactivate-cancel">Cancel</Button>
          <Button onClick={doIt} disabled={busy || eligible.length === 0}
                  data-testid="deactivate-confirm"
                  className="bg-[#c9a93a] text-[#013523] hover:bg-[#daa64a]">
            {busy ? 'Deactivating…' : `Deactivate ${eligible.length}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== Bulk Delete Dialog ====================
function BulkDeleteDialog({ selected, onClose, onDone }) {
  const [preview, setPreview] = useState(null); // { safe, blocked, missing }
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminAPI.deletePreview(selected.map((m) => m.profile_id));
        setPreview(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to classify selection');
        setPreview({ safe: [], blocked: [] });
      }
    })();
  }, [selected]);

  const doIt = async () => {
    if (typed !== 'DELETE') return;
    setBusy(true);
    try {
      const ids = preview.safe.map((s) => s.profile_id);
      const r = await adminAPI.bulkDelete(ids);
      toast.success(`Permanently deleted ${r.data.count} member(s).`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bulk-delete-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#9c2626]">Permanently delete members?</DialogTitle>
        </DialogHeader>
        {!preview ? (
          <div className="px-5 pb-4 sm:px-6 py-6 text-center text-[#6a766e] text-sm">Classifying members…</div>
        ) : (
          <div className="px-5 pb-4 sm:px-6 space-y-3 text-[13px] text-[#2a332c] max-h-[65vh] overflow-y-auto">
            <div className="rounded-lg border border-[#f0b7b7] bg-[#fdf5f5] p-3 text-[#9c2626] text-[12px]">
              Deleted accounts, their auth credentials and pending invites are removed. Historical match/tournament/ladder records are preserved elsewhere. This action cannot be undone.
            </div>

            {preview.safe.length > 0 && (
              <div>
                <div className="text-[12px] text-[#6a766e] uppercase tracking-wider mb-1">
                  Safe to delete ({preview.safe.length})
                </div>
                <ul className="bg-[#f0f7f2] border border-[#b7e0c1] rounded-lg p-3 text-[12.5px] space-y-1"
                    data-testid="delete-safe-list">
                  {preview.safe.map((s) => (
                    <li key={s.profile_id}>· <span className="font-medium">{s.full_name}</span>
                      {s.email && <span className="text-[#6a766e]"> — {s.email}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {preview.blocked.length > 0 && (
              <div>
                <div className="text-[12px] text-[#8a6d1a] uppercase tracking-wider mb-1">
                  Blocked from deletion ({preview.blocked.length}) — deactivate instead
                </div>
                <ul className="bg-[#fffbf0] border border-[#eddb9f] rounded-lg p-3 text-[12.5px] space-y-1.5"
                    data-testid="delete-blocked-list">
                  {preview.blocked.map((b) => (
                    <li key={b.profile_id}>
                      · <span className="font-medium">{b.full_name}</span>
                      {b.email && <span className="text-[#6a766e]"> — {b.email}</span>}
                      <ul className="text-[11.5px] text-[#8a6d1a] pl-4 list-disc list-inside mt-0.5">
                        {(b.reasons || []).map((r, i) => (<li key={i}>{r}</li>))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {preview.safe.length > 0 && (
              <div>
                <label className="block text-[11.5px] text-[#6a766e] mb-1.5 uppercase tracking-wider">
                  Type <span className="font-mono font-semibold text-[#9c2626]">DELETE</span> to confirm
                </label>
                <input
                  data-testid="delete-typed-confirm"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  autoFocus
                  className="w-full bg-white border border-[#e3dcd1] rounded-lg px-3 py-2 text-[14px] font-mono focus:outline-none focus:ring-2 focus:ring-[#9c2626]/20 focus:border-[#9c2626]"
                  placeholder="DELETE"
                />
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="delete-cancel">Cancel</Button>
          <Button onClick={doIt}
                  disabled={busy || !preview || preview.safe.length === 0 || typed !== 'DELETE'}
                  data-testid="delete-confirm"
                  className="bg-[#9c2626] text-white hover:bg-[#7a1d1d] disabled:opacity-50">
            {busy ? 'Deleting…' : preview ? `Permanently delete ${preview.safe.length}` : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ==================== Gender inline editor (drawer) ====================
function GenderInlineEditor({ profile, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [val, setVal] = useState(profile.gender || null);

  const save = async (g) => {
    setBusy(true);
    try {
      await adminAPI.setMemberGender(profile.id, g);
      setVal(g);
      toast.success(g ? `Gender set to ${g === 'male' ? 'Male' : 'Female'}` : 'Gender cleared');
      await onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save gender');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="drawer-gender-editor"
      className="flex items-center justify-between text-[12.5px] bg-white border border-[#e3dcd1] rounded-xl px-3 py-2 gap-2"
    >
      <span className="text-[#6a766e] shrink-0">Gender</span>
      <div className="flex items-center gap-1.5">
        {['male', 'female'].map((g) => (
          <button
            key={g}
            type="button"
            disabled={busy}
            onClick={() => save(g)}
            data-testid={`drawer-gender-${g}`}
            className={`text-[11px] px-2.5 py-1 rounded-full border transition ${
              val === g
                ? 'bg-[#015e3f] text-white border-[#015e3f]'
                : 'bg-white text-[#013523] border-[#e3dcd1] hover:border-[#013523]/40'
            } disabled:opacity-50`}
          >
            {g === 'male' ? 'Male' : 'Female'}
          </button>
        ))}
        {val && (
          <button
            type="button"
            disabled={busy}
            onClick={() => save(null)}
            data-testid="drawer-gender-clear"
            className="text-[11px] px-2 py-1 rounded-full text-[#6a766e] hover:text-[#9c2626] disabled:opacity-50"
            title="Clear"
          >
            <X size={12} weight="bold" />
          </button>
        )}
      </div>
    </div>
  );
}

// ==================== Bulk Gender Dialog ====================
function BulkGenderDialog({ selected, gender, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  // NULL-only preview client-side. Backend re-enforces this rule
  // authoritatively so a stale-selection cannot bypass it.
  const nullOnly = selected.filter((m) => !m.gender);
  const alreadySet = selected.filter((m) => !!m.gender);
  const alreadyCorrect = alreadySet.filter((m) => m.gender === gender);
  const conflicting = alreadySet.filter((m) => m.gender !== gender);

  const doIt = async () => {
    setBusy(true);
    try {
      const ids = selected.map((m) => m.profile_id);
      const r = await adminAPI.bulkSetGender(ids, gender, false);
      toast.success(
        `Updated ${r.data.updated} member(s). Skipped ${r.data.skipped} with an existing value.`
      );
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk update failed');
    } finally { setBusy(false); }
  };

  const label = gender === 'male' ? 'Male' : 'Female';

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="bulk-gender-dialog">
        <DialogHeader>
          <DialogTitle>Set gender = {label} for {selected.length} member{selected.length === 1 ? '' : 's'}?</DialogTitle>
        </DialogHeader>
        <div className="px-5 pb-4 sm:px-6 text-[13px] text-[#2a332c] space-y-2">
          <p className="text-[12.5px]">
            <b>Safe / NULL-only.</b> Only members whose gender is currently
            unset will be updated. Members who already have a gender value
            will be skipped — never overwritten.
          </p>
          <ul className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-lg p-3 space-y-1 text-[12.5px]">
            <li>· <b>{nullOnly.length}</b> will be set to <b>{label}</b></li>
            {alreadyCorrect.length > 0 && (
              <li className="text-[#6a766e]">· {alreadyCorrect.length} already = {label} (no change)</li>
            )}
            {conflicting.length > 0 && (
              <li className="text-[#8a6d1a]">
                · {conflicting.length} already have a different gender — <b>skipped</b>
              </li>
            )}
          </ul>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="bulk-gender-cancel">Cancel</Button>
          <Button
            onClick={doIt}
            disabled={busy || nullOnly.length === 0}
            data-testid="bulk-gender-confirm"
            className="bg-[#015e3f] text-white hover:bg-[#013523]"
          >
            {busy ? 'Updating…' : `Set ${nullOnly.length} to ${label}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
